import { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback, useMemo } from 'react';
import { getTeacherProfile } from '@/services/teacherProfileService';
import { getProfile, AccountDeactivatedError } from '@/services/UserManagmentServices';
import { useAuth } from '@/hooks/use-auth-compat';
import type { VersionStatus, ProfileUIResponse, VersionData, SectionView } from '@/types/teacherProfile';
import { useRole } from '@/contexts/RoleContext';
import { useQueryClient } from '@tanstack/react-query';

interface TeacherProfileContextType {
  /** Current editable version data (draft if exists, else active) */
  teacherProfile: VersionData | null;
  userId: string | null;
  setTeacherProfile: (profile: VersionData | null) => void;
  refreshProfile: (force?: boolean) => Promise<void>;
  isLoading: boolean;
  // Full response access
  profileResponse: ProfileUIResponse | null;
  sections: SectionView[];
  // Version-aware state
  hasActiveVersion: boolean;
  hasDraft: boolean;
  draftStatus: VersionStatus | null;
  isFirstTimeTeacher: () => boolean;
  canEdit: () => boolean;
  canCreateDraft: () => boolean;
  /** Call on route navigation to trigger a profile refresh if pending review */
  onNavigate: () => void;
}

const TeacherProfileContext = createContext<TeacherProfileContextType | undefined>(undefined);

export const TeacherProfileProvider = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();
  const { setRole } = useRole();
  const queryClient = useQueryClient();
  const [teacherProfile, setTeacherProfile] = useState<VersionData | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [profileResponse, setProfileResponse] = useState<ProfileUIResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const teacherProfileRef = useRef<VersionData | null>(null);
  const currentTokenRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const handleSetTeacherProfile = useCallback((profile: VersionData | null) => {
    teacherProfileRef.current = profile;
    setTeacherProfile(profile);
  }, []);

  const refreshProfile = useCallback(async (force = false) => {
    const token = await auth.getToken();
    if (!token) return;

    if (!force && teacherProfileRef.current && token === currentTokenRef.current) return;
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    currentTokenRef.current = token;
    setIsLoading(true);
    try {
      const response = await getTeacherProfile(token);
      setProfileResponse(response);
      setUserId(response?.profile?.id || null);

      // Current editable version: prefer draft, fall back to active
      const currentData = response?.profile?.draftVersion?.data
        ?? response?.profile?.activeVersion?.data
        ?? null;
      handleSetTeacherProfile(currentData);
    } catch (error) {
      console.error('Failed to refresh teacher profile:', error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [auth.getToken, handleSetTeacherProfile]);

  // Only fetch the teacher profile when the authenticated user is actually a teacher.
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await auth.getToken();
        if (!token || cancelled) return;
        const me = await queryClient.fetchQuery({
          queryKey: ['currentUser'],
          queryFn: () => getProfile(token),
          staleTime: 5 * 60 * 1000,
        });
        if (cancelled) return;
        if (me.defaultRole === 'TEACHER') {
          setRole('teacher');
          refreshProfile();
        } else if (me.defaultRole === 'STUDENT') {
          setRole('student');
        }
      } catch (err) {
        if (err instanceof AccountDeactivatedError) {
          window.location.replace('/account-deactivated');
          return;
        }
        // 404 (unprovisioned) / 401 / network — swallow; OAuthCallback handles UX.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, auth.getToken, refreshProfile]);

  // Derived state from profileResponse
  const hasActiveVersion = useMemo(() => {
    return profileResponse?.profile?.activeVersion != null;
  }, [profileResponse]);

  const hasDraft = useMemo(() => {
    return profileResponse?.profile?.draftVersion != null;
  }, [profileResponse]);

  const draftStatus = useMemo((): VersionStatus | null => {
    return profileResponse?.profile?.draftVersion?.status ?? null;
  }, [profileResponse]);

  const sections = useMemo((): SectionView[] => {
    return profileResponse?.sections ?? [];
  }, [profileResponse]);

  const isFirstTimeTeacher = useCallback(() => {
    return !hasActiveVersion && !hasDraft;
  }, [hasActiveVersion, hasDraft]);

  const canEdit = useCallback(() => {
    if (!hasActiveVersion) return true;
    if (hasDraft && (draftStatus === 'DRAFT' || draftStatus === 'REJECTED')) return true;
    return false;
  }, [hasActiveVersion, hasDraft, draftStatus]);

  const canCreateDraft = useCallback(() => {
    return hasActiveVersion && !hasDraft;
  }, [hasActiveVersion, hasDraft]);

  // ── Auto-refresh while profile is awaiting admin review ──────────
  // Re-fetches on route navigation and tab visibility changes so the
  // teacher sees the approved status without a manual browser refresh.
  // Disabled once an active version exists (profile already approved).
  const needsStatusRefresh = !hasActiveVersion && draftStatus === 'PENDING_REVIEW';
  const lastAutoRefreshRef = useRef(0);
  const THROTTLE_MS = 30_000;

  const throttledRefresh = useCallback(() => {
    if (!needsStatusRefresh) return;
    const now = Date.now();
    if (now - lastAutoRefreshRef.current < THROTTLE_MS) return;
    lastAutoRefreshRef.current = now;
    refreshProfile(true);
  }, [refreshProfile, needsStatusRefresh]);

  // Trigger 1: tab becomes visible again
  useEffect(() => {
    if (!needsStatusRefresh) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') throttledRefresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [needsStatusRefresh, throttledRefresh]);

  return (
    <TeacherProfileContext.Provider value={{
      teacherProfile,
      userId,
      setTeacherProfile: handleSetTeacherProfile,
      refreshProfile,
      isLoading,
      profileResponse,
      sections,
      hasActiveVersion,
      hasDraft,
      draftStatus,
      isFirstTimeTeacher,
      canEdit,
      canCreateDraft,
      onNavigate: throttledRefresh,
    }}>
      {children}
    </TeacherProfileContext.Provider>
  );
};

export const useTeacherProfile = () => {
  const context = useContext(TeacherProfileContext);
  if (!context) {
    throw new Error('useTeacherProfile must be used within TeacherProfileProvider');
  }
  return context;
};

